import type { RuleResult } from "@kwp/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { domainError, errorMessage, useTRPC } from "../../../lib/api";
import { useMe } from "../../../lib/me";
import { usePresence } from "../../../lib/use-presence";
import { Button, ErrorText, StatusText, TextField } from "../../../ui";
import { Dialog } from "../../../ui/overlay";
import { useToast } from "../../../ui/toast";
import { newRequestId } from "../../tasks/use-task-actions";
import { isDestructiveDraft, scopeInput, toActions, type RuleDraft } from "./model";
import { ActionsBuilder, ScopePicker } from "./RuleFields";

const PREVIEW_DELAY_MS = 400;

/** The query as typed, settled for a moment, so the preview does not search on every keystroke. */
function useSettled(value: string): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), PREVIEW_DELAY_MS);
    return () => clearTimeout(t);
  }, [value]);
  return settled;
}

/** How many messages match now and a few of them, as the person types the query (§7). */
function Preview({ query, scope }: { query: string; scope: { workspaceId?: string; accountIds?: string[] } }) {
  const trpc = useTRPC();
  const settled = useSettled(query.trim());
  const preview = useQuery({ ...trpc.rules.preview.queryOptions({ query: settled, ...scope }), enabled: settled.length > 0, retry: false });
  if (settled.length === 0) return <p className="text-sm text-muted">Type a query to see what it matches.</p>;
  if (preview.isPending) return <StatusText>Counting matching mail…</StatusText>;
  if (preview.isError) {
    const reason = domainError(preview.error)?.reason;
    return <ErrorText>{reason === "query_syntax" || reason === "no_accounts" ? errorMessage(preview.error) : `Preview failed: ${errorMessage(preview.error)}`}</ErrorText>;
  }
  const p = preview.data;
  return (
    <div className="flex flex-col gap-1" aria-label="Matching mail">
      <StatusText>
        {p.matched}
        {p.more ? "+" : ""} message{p.matched === 1 && !p.more ? "" : "s"} match now.
      </StatusText>
      {p.sample.length > 0 ? (
        <ul className="flex flex-col gap-0.5 text-xs text-muted">
          {p.sample.slice(0, 5).map((s, i) => (
            <li key={i} className="truncate">
              {s.from} · {s.subject}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Create or edit a rule: query with live preview, actions, scope (architecture §7). */
export function RuleDialog({ draft, onClose }: { draft: RuleDraft; onClose: () => void }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const toast = useToast();
  const me = useMe();
  const { ensurePresence } = usePresence();
  const nameRef = useRef<HTMLInputElement>(null);
  const accounts = useQuery(trpc.accounts.list.queryOptions());
  const [name, setName] = useState(draft.name);
  const [query, setQuery] = useState(draft.query);
  const [actions, setActions] = useState(draft.actions);
  const [scope, setScope] = useState(draft.scope);
  const [problem, setProblem] = useState<string | null>(null);
  const create = useMutation(trpc.rules.create.mutationOptions());
  const update = useMutation(trpc.rules.update.mutationOptions());
  const busy = create.isPending || update.isPending;
  const destructive = isDestructiveDraft(actions);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = toActions(actions);
    if ("error" in parsed) {
      setProblem(parsed.error);
      return;
    }
    if (scope.kind === "accounts" && scope.accountIds.length === 0) {
      setProblem("Pick at least one account.");
      return;
    }
    setProblem(null);
    try {
      // A rule that trashes or forwards is destructive: with a passkey it is saved at once,
      // otherwise it waits in Approvals.
      if (destructive && (await ensurePresence()) === "cancelled") {
        setProblem("Not saved: the passkey check was cancelled.");
        return;
      }
      const trimmed = name.trim();
      const input = { name: trimmed.length === 0 ? query.trim().slice(0, 120) : trimmed, query: query.trim(), actions: parsed.actions, ...scopeInput(scope) };
      const out =
        draft.ruleId === null
          ? await create.mutateAsync({ input, requestId: newRequestId() })
          : await update.mutateAsync({ input: { ...input, ruleId: draft.ruleId }, requestId: newRequestId() });
      await queryClient.invalidateQueries({ queryKey: trpc.rules.list.queryKey() });
      if (out.status === "awaiting_approval") toast({ message: "Waiting for your approval in Approvals (it trashes or forwards mail)." });
      else {
        const result = out.result as RuleResult;
        if (result.lastError !== null) toast({ tone: "error", message: `Saved but kept off: ${result.lastError}` });
        else toast({ message: result.mode === "native" ? "Rule saved. It runs in your mail provider too." : "Rule saved. KitWork+ runs it on new mail." });
      }
      onClose();
    } catch (error) {
      setProblem(errorMessage(error));
    }
  }

  return (
    <Dialog title={draft.ruleId === null ? "New rule" : "Edit rule"} description="Rules act on new mail as it arrives." onClose={onClose} initialFocus={nameRef}>
      <form onSubmit={submit} className="flex flex-col gap-4" aria-label={draft.ruleId === null ? "New rule" : "Edit rule"}>
        <TextField ref={nameRef} label="Name" maxLength={120} placeholder="Receipts" value={name} onChange={(e) => setName(e.target.value)} />
        <TextField
          label="When mail matches"
          hint="Search syntax, e.g. from:shop.example subject:receipt"
          required
          maxLength={2000}
          autoComplete="off"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Preview query={query} scope={scopeInput(scope)} />
        <ActionsBuilder value={actions} onChange={setActions} />
        {destructive ? <p className="text-sm text-muted">Trashing or forwarding mail needs your passkey (or approval) to save.</p> : null}
        <ScopePicker value={scope} onChange={setScope} accounts={accounts.data === undefined ? [] : accounts.data} workspaces={me.workspaces} />
        <div className="min-h-5">{problem === null ? null : <ErrorText>{problem}</ErrorText>}</div>
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={busy || query.trim().length === 0}>
            {busy ? "Saving…" : "Save rule"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
